import { useQueryClient } from '@tanstack/react-query'
import type { RedeemPost, RedeemPostPlatform } from '@/api/client'

export const PLATFORM_LABEL: Record<RedeemPostPlatform, string> = {
  reddit: 'Reddit',
  ppomppu: '뽐뿌',
  okky: 'OKKY',
  x: 'X',
  threads: 'Threads',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  bluesky: 'Bluesky',
}

/** Where a desk post goes, in words: the subreddit, or the Bluesky account. */
export function where(post: Pick<RedeemPost, 'platform' | 'channel'>): string {
  return post.platform === 'bluesky' ? 'Bluesky' : `r/${post.channel}`
}

/** The query keys a redeem write can change: the pools and their counts, the
 *  request queue, the desk's health and numbers, and the posts that carry the
 *  request counts. */
export const REDEEM_KEYS = [
  'redeemApps',
  'redeemApp',
  'redeemCodes',
  'requests',
  'deskHealth',
  'deskStats',
  'deskControls',
  'redeemPosts',
  'redeemPost',
  'schedule',
] as const

export function useInvalidateRedeem() {
  const qc = useQueryClient()
  return () => Promise.all(REDEEM_KEYS.map((k) => qc.invalidateQueries({ queryKey: [k] })))
}

type Named = { name: string; slug: string }

const nameKey = (s: string) => s.trim().toLowerCase()

/** True when another row carries the same name. The same product shows up as
 *  several rows (a lifetime giveaway that ended, then a Halloween one), and
 *  the name alone does not say which. */
export function sharesName(app: Named, apps: readonly Named[]): boolean {
  const k = nameKey(app.name)
  return apps.some((a) => a.slug !== app.slug && nameKey(a.name) === k)
}

/** The row's name as text: "Korio · korio-hw26" when another row has the same
 *  name, plain "Korio" otherwise. The one place this rule lives. */
export function appLabel(app: Named, apps: readonly Named[]): string {
  return sharesName(app, apps) ? `${app.name} · ${app.slug}` : app.name
}

/** Rows of one product: the same store id, or the same name when the row has
 *  no store id (a web product). */
export function sameProduct(
  a: { name: string; store_id: string; slug: string },
  b: { name: string; store_id: string; slug: string },
): boolean {
  if (a.slug === b.slug) return false
  if (a.store_id || b.store_id) return a.store_id === b.store_id
  return nameKey(a.name) === nameKey(b.name)
}
